import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod, paginate } from '@bc-library/interface'
import { stringifyQuery } from 'vue-router'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import { useUserStore } from '@/config/store/userStore'
import { afterReceiving, afterReceivingCampaignCoverage, afterReceivingCampaignDetails, afterReceivingShowCampaign, beforeSending } from './hooks'

const resource = 'evaluationCampaigns'

const paginated = (axios: IFactoryFunctionRequest<paginate<any>, undefined>) => ({ search = '', closedStatus = 'open' }: { search?: string, closedStatus?: string } = {}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/`,
    query: { search, closedStatus },
  })
}



const filter = (axios: IFactoryFunctionRequest<any, any>) => ({ page = 0, search = '', limit = 10 }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/filter`,
    query: { page, search, limit },
  })
}


const createCampaign = (axios: IFactoryFunctionRequest<any, any>) => ({ data }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}`,
    data,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceivingOne => afterReceivingOne,
  })
}

type TCampaignQuery = {
  offices?: string[]
  teams?: string[]
  jobs?: string[]
  jobCategories?: string[]
  evaluators?: string[]
  resourceOffices?: string[]
  resourceTeams?: string[]
  resourceJobs?: string[]
  resourceJobCategories?: string[]
  assesees?: string[]
  projects?: string[]
  clients?: string[]
  createdSinceDays?: number
}

const showCampaign = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId, query }: { campaignId: string, query?: TCampaignQuery }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}`,
    query,
    afterReceivingHook: afterReceivingShowCampaign,
  })
}

type TScaleSetting = {
  scaleKey: string
  negativeValues: number[]
  positiveValues: number[]
}

const updateScaleSettings = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId, scaleSettings }: { campaignId: string, scaleSettings: TScaleSetting[] }) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/scaleSettings`,
    data: { scaleSettings },
  })
}

export type TCampaignCoverageSettings = {
  enabled: boolean
  minDays: number
  source: 'team' | 'staffing' | 'timesheet' | 'either'
  startDate: Date | null
  endDate: Date | null
}

// The campaign page loads the campaign first, then its analysis and coverage (slower) in parallel
const showCampaignAnalysis = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId, query }: { campaignId: string, query?: TCampaignQuery }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/analysis`,
    query,
  })
}

// Negative answers per criterion of another campaign, to compare with
const showCampaignAnalysisSummary = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId, query }: { campaignId: string, query?: TCampaignQuery }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/analysis`,
    query: { ...query, summary: 1 },
  })
}

const showCampaignCoverage = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId, query }: { campaignId: string, query?: TCampaignQuery }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/coverage`,
    query,
    afterReceivingHook: afterReceivingCampaignCoverage,
  })
}

const showCampaignDetails = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId, query }: { campaignId: string, query?: TCampaignQuery }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/details`,
    query,
    afterReceivingHook: afterReceivingCampaignDetails,
  })
}

const exportCampaignDetails = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId, query }: { campaignId: string, query?: TCampaignQuery }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/details/export`,
    query,
  })
}

const updateCampaign = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId, data }: { campaignId: string, data: { display: string, startDate: Date | string | null, endDate: Date | string | null, coverage?: TCampaignCoverageSettings } }) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}`,
    data,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}

const analyseQuestion = (axios: IFactoryFunctionRequest<any, any>) => (questionContent) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/analyseQuestion`,
    data: {answers: questionContent},
  })
}

const analyseQualitativeQuestion = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId, data, query }: { campaignId: string, data: { formularId: string, calcId: string, analysisIntent?: string }, query?: TCampaignQuery }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/qualitative/analyse`,
    data,
    query,
  })
}

const analyseAllQualitativeQuestions = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId, query }: { campaignId: string, query?: TCampaignQuery }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/qualitative/analyse-all`,
    query,
  })
}

export type TQualitativeChatAnalysis = {
  summary?: string | null
  sentiment?: 'positive' | 'negative' | 'neutral'
  averagePositiveFeedBack?: number | null
  cloudTags?: { tag: string, weight: number, positiveFeedBack: number | null }[]
  answerSentiments?: { answerId: string, positiveFeedBack: number }[]
}

export type TQualitativeChatContext = {
  overview: {
    question: { display?: string | null, formularDisplay?: string | null, context?: string[] }
    totals: {
      answers: number
      totalAnswers: number
      respondents: number
      projects: number
      evaluations: number
      answersWithoutProject: number
      truncated: boolean
    }
    sentiment: {
      averagePositiveFeedBack: number | null
      distribution: { positive: number, neutral: number, negative: number, unscored: number }
    }
    topProjects: { projectId: string, project: string | null, answers: number, respondents: number }[]
  }
  rights: { staffing: boolean, leave: boolean, leaveAll: boolean, hrStats: boolean }
  suggestedPrompts: string[]
}

export type TQualitativeChatStreamHandlers = {
  onToken?: (text: string) => void
  onEvent?: (event: Record<string, unknown>) => void
  onDone?: (payload: {
    answer: string
    uiBlocks?: unknown[]
    tools?: { id: string, name: string, streaming?: boolean }[]
  }) => void
  onError?: (message: string) => void
  signal?: AbortSignal
}

const qualitativeChatContext = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId, formularId, calcId, query }: { campaignId: string, formularId: string, calcId: string, query?: TCampaignQuery }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/qualitative/chat/context`,
    query: { ...query, formularId, calcId },
  })
}

/**
 * SSE chat over one qualitative question. Raw fetch because the shared axios
 * factory has no streaming path — query serialization matches useAxios so the
 * backend resolves the exact same cohort as showCampaign.
 */
const qualitativeChatStream = () => async (
  { campaignId, formularId, calcId, question, messages, language, analysis, query }: {
    campaignId: string
    formularId: string
    calcId: string
    question: string
    messages?: { role: 'user' | 'assistant', content: string }[]
    language?: string
    analysis?: TQualitativeChatAnalysis | null
    query?: TCampaignQuery
  },
  handlers: TQualitativeChatStreamHandlers = {},
) => {
  const userStore = useUserStore()
  const token = userStore?.token
  if (!token) throw new Error('Missing auth token')

  const search = query && Object.keys(query).length ? `?${stringifyQuery(query as any)}` : ''
  const response = await fetch(
    `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/qualitative/chat/stream${search}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ formularId, calcId, question, messages, language, analysis }),
      signal: handlers.signal,
    },
  )

  if (!response.ok) {
    let message = `Qualitative chat failed (${response.status})`
    try {
      const errBody = await response.json()
      if (errBody?.customMessage) message = errBody.customMessage
    }
    catch {
      // ignore parse errors
    }
    throw new Error(message)
  }

  if (!response.body) throw new Error('Qualitative chat stream empty body')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let receivedDone = false

  const handleEvent = (event: any) => {
    if (!event || typeof event !== 'object') return
    if (event.type === 'token' && typeof event.text === 'string') {
      handlers.onToken?.(event.text)
      return
    }
    if (event.type === 'tool' || event.type === 'reasoning' || event.type === 'ui') {
      handlers.onEvent?.(event)
      return
    }
    if (event.type === 'done') {
      receivedDone = true
      handlers.onDone?.({
        answer: event.answer || '',
        uiBlocks: Array.isArray(event.uiBlocks) ? event.uiBlocks : [],
        tools: Array.isArray(event.tools) ? event.tools : [],
      })
      return
    }
    if (event.type === 'error') {
      handlers.onError?.(event.message || 'Qualitative chat failed')
    }
  }

  const flushChunk = (chunk: string) => {
    const dataLine = chunk
      .split('\n')
      .map(line => line.trimEnd())
      .find(line => line.startsWith('data:'))
    if (!dataLine) return
    const raw = dataLine.replace(/^data:\s?/, '')
    if (!raw || raw === '[DONE]') return
    try {
      handleEvent(JSON.parse(raw))
    }
    catch {
      // ignore malformed SSE frames
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const chunks = buffer.split('\n\n')
    buffer = chunks.pop() || ''
    chunks.forEach(flushChunk)
  }

  if (buffer.trim()) flushChunk(buffer)

  if (!receivedDone) throw new Error('Qualitative chat ended without done event')
}

const sendReminders = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId, query }: { campaignId: string, query?: TCampaignQuery }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/sendReminders`,
    query,
  })
}

const closeCampaign = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId }: { campaignId: string }) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/close`,
    afterReceivingHook: afterReceiving,
  })
}

const reopenCampaign = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId }: { campaignId: string }) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}/reopen`,
    afterReceivingHook: afterReceiving,
  })
}

const deleteCampaign = (axios: IFactoryFunctionRequest<any, any>) => ({ campaignId }: { campaignId: string }) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${campaignId}`,
  })
}

export default (axios: AxiosInstance) => ({
  paginated: paginated(useAxios(axios)),
  filter: filter(useAxios(axios)),
  createCampaign: createCampaign(useAxios(axios)),
  showCampaign: showCampaign(useAxios(axios)),
  showCampaignAnalysis: showCampaignAnalysis(useAxios(axios)),
  showCampaignAnalysisSummary: showCampaignAnalysisSummary(useAxios(axios)),
  showCampaignCoverage: showCampaignCoverage(useAxios(axios)),
  showCampaignDetails: showCampaignDetails(useAxios(axios)),
  exportCampaignDetails: exportCampaignDetails(useAxios(axios)),
  updateCampaign: updateCampaign(useAxios(axios)),
  updateScaleSettings: updateScaleSettings(useAxios(axios)),
  analyseQuestion: analyseQuestion(useAxios(axios)),
  analyseQualitativeQuestion: analyseQualitativeQuestion(useAxios(axios)),
  analyseAllQualitativeQuestions: analyseAllQualitativeQuestions(useAxios(axios)),
  qualitativeChatContext: qualitativeChatContext(useAxios(axios)),
  qualitativeChatStream: qualitativeChatStream(),
  sendReminders: sendReminders(useAxios(axios)),
  closeCampaign: closeCampaign(useAxios(axios)),
  reopenCampaign: reopenCampaign(useAxios(axios)),
  deleteCampaign: deleteCampaign(useAxios(axios)),
})
